import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowUpRight, Loader2, CalendarOff } from "lucide-react";
import PageWrapper from "@/components/layout/PageWrapper";
import Breadcrumb from "@/components/Breadcrumb";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const Events = () => {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/events`)
      .then((r) => mounted && setEvents(r.data || []))
      .catch(() => mounted && setEvents([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <PageWrapper
      title="Events & Activities — ADHD School Bhadrak"
      description="Workshops, awareness camps and celebrations at ADHD School Bhadrak."
    >
      <section className="adhd-container pt-10 lg:pt-14">
        <Breadcrumb pageKey="nav.events" />
        <p className="adhd-eyebrow mt-6">
          <Calendar size={13} /> What's on
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-[color:var(--adhd-text)]">
          {t("events.title")}
        </h1>
        <p className="mt-3 text-[color:var(--adhd-muted)] max-w-2xl">{t("events.subtitle")}</p>
      </section>

      <section className="adhd-container mt-10 mb-10">
        {loading ? (
          <div className="adhd-card p-10 text-center text-[color:var(--adhd-muted)] flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading events…
          </div>
        ) : events.length === 0 ? (
          <div
            data-testid="events-empty"
            className="adhd-card p-10 text-center text-[color:var(--adhd-muted)] flex flex-col items-center justify-center gap-3"
          >
            <CalendarOff size={28} style={{ color: "var(--adhd-primary)" }} />
            <p className="font-serif text-xl text-[color:var(--adhd-text)]">No events to show right now</p>
            <p className="text-sm max-w-md">
              New workshops and activities are added every season. Please check back soon or contact us to know what's coming up.
            </p>
            <Link to="/contact" data-testid="events-empty-cta" className="adhd-btn-secondary mt-2 !py-2.5">
              Contact us <ArrowUpRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((e, i) => (
              <article
                key={e.id}
                data-testid={`event-card-${i}`}
                className="adhd-card overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={e.image}
                    alt={e.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className="px-2.5 py-1 rounded-full font-semibold"
                      style={{
                        background: "rgba(58,124,165,0.12)",
                        color: "var(--adhd-primary)",
                      }}
                    >
                      {e.category}
                    </span>
                    <span className="text-[color:var(--adhd-muted)]">{formatDate(e.date)}</span>
                  </div>
                  <h3 className="font-serif text-xl text-[color:var(--adhd-text)] leading-snug">
                    {e.title}
                  </h3>
                  <p className="text-sm text-[color:var(--adhd-muted)] leading-relaxed flex-1">
                    {e.description}
                  </p>
                  <Link
                    to="/contact"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--adhd-primary)] hover:underline"
                    data-testid={`event-cta-${i}`}
                  >
                    Register interest <ArrowUpRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </PageWrapper>
  );
};

export default Events;
